import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Bulletin {
  readonly bulletinId: string;
  readonly title: string;
  readonly category: string;
  readonly createdAt: string;
}

// Mirrors backend M27BulletinCategory — server rejects any other value.
const BULLETIN_CATS = ['CORPORATE', 'DEPARTMENT', 'REGIONAL', 'VENDOR', 'COMPLIANCE', 'TECHNOLOGY', 'TRAINING'];

/** M27 — Bulletin Board (§31). Lists bulletins with a category filter and
 * supports posting new bulletins over bulletin.controller (BULLETIN_MANAGE). */
export function M27BulletinBoardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Bulletin[]>([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [newCategory, setNewCategory] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const q = category ? `?category=${encodeURIComponent(category)}` : '';
    try {
      const r = await apiRequest<{ data: Bulletin[]; total: number }>(`/v1/collab/bulletins${q}`, { token });
      setRows(r.data); setTotal(r.total); setError(null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [token, category]);

  useEffect(() => { void load(); }, [load]);

  async function create(): Promise<void> {
    if (!title.trim() || !body.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest('/v1/collab/bulletins', {
        token, method: 'POST',
        body: { title, body, category: newCategory || undefined },
      });
      setTitle(''); setBody(''); setNewCategory('');
      setNotice(t('m27.bulletin.noticePosted'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="bulletin-board">
      <h1>{t('m27.bulletin.heading')}</h1>

      <form aria-label="bulletin-create-form" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <label htmlFor="bul-title">{t('m27.bulletin.fldTitle')}</label>
        <input id="bul-title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <label htmlFor="bul-body">{t('m27.bulletin.fldBody')}</label>
        <textarea id="bul-body" value={body} onChange={(e) => setBody(e.target.value)} required />
        <label htmlFor="bul-new-category">{t('m27.bulletin.fldNewCategory')}</label>
        <select id="bul-new-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
          <option value="">{t('m27.bulletin.optDefaultCorporate')}</option>
          {BULLETIN_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button type="submit" disabled={busy}>{t('m27.bulletin.btnCreate')}</button>
      </form>

      <label htmlFor="bul-category">{t('m27.bulletin.fldCategory')}</label>
      <select id="bul-category" value={category} onChange={(e) => setCategory(e.target.value)}>
        {['', 'CORPORATE', 'DEPARTMENT', 'REGIONAL', 'VENDOR', 'COMPLIANCE', 'TECHNOLOGY', 'TRAINING'].map((c) => <option key={c || 'all'} value={c}>{c || t('m27.bulletin.optAll')}</option>)}
      </select>
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.bulletin.loading')}</p> : (
        <>
          <p aria-label="bulletin-count">{t('m27.bulletin.count', { count: total })}</p>
          <table aria-label="bulletin-table">
            <thead><tr><th>{t('m27.bulletin.fldTitle')}</th><th>{t('m27.bulletin.fldCategory')}</th></tr></thead>
            <tbody>{rows.map((b) => <tr key={b.bulletinId}><td>{b.title}</td><td>{b.category}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </main>
  );
}
